/**
 * ScrollToTopButton — floating control that returns a long, paginated list to
 * the top (mobile buddySearchResult / aiBuddy parity).
 *
 * Hidden until the member is far enough down that scrolling back would be a
 * chore, so short lists never grow a control they do not need.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { ChevronUpIcon } from './icons';
import './scrollToTopButton.css';

/** Scroll depth past which returning to the top is worth a shortcut. */
const REVEAL_AFTER_PX = 300;

export interface ScrollToTopButtonProps {
  /** Hidden regardless of scroll depth — e.g. while the list is empty. */
  enabled?: boolean;
}

export function ScrollToTopButton({ enabled = true }: ScrollToTopButtonProps) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setVisible(false);
      return;
    }
    const sync = () => setVisible(window.scrollY > REVEAL_AFTER_PX);
    sync();
    window.addEventListener('scroll', sync, { passive: true });
    return () => window.removeEventListener('scroll', sync);
  }, [enabled]);

  if (!enabled || !visible) return null;

  return (
    <button
      type="button"
      className="scroll-to-top"
      aria-label={t('common.accessibility.scrollToTop')}
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
    >
      <ChevronUpIcon size={20} />
    </button>
  );
}
